/**
 * Spike brush: outline-line paint (like grass), theme spike tiles, inside-corner editor.
 *
 * Painted marks live on the solid cell (`spikeLines`, same bit packing as GrassLines). Visuals
 * go in the empty neighbour (the dest cell). Inside corners are two dest-mapping lines that
 * share one empty cell — one sprite, two collider sides. Slopes (`LINE_S`) cannot take spikes.
 */
import type { LevelDoc, PaletteEntry, SpikeLines } from '../model/format.ts';
import { LINE_B, LINE_L, LINE_R, LINE_T } from '../model/format.ts';
import { gameData } from '../data/gameData.ts';
import { customTileOf } from '../data/customTiles.ts';
import { styleOverride, surfaceStyleAt, type SpikeCorner, type StyleOverride, type TerrainContainer } from '../terrain/index.ts';
import { applyGrassLines, lineMark, surfaceLines, type GrassMode, type SurfaceLine } from './grass.ts';
import type { TileEditRequest } from './store.ts';

export type { SpikeLines };
export type SpikeMode = 'paint' | 'erase';
export type SpikeDir = 'floor' | 'ceiling' | 'left' | 'right';
export type { SpikeCorner };

export const SPIKE_SIDES = [LINE_L, LINE_R, LINE_B, LINE_T];

/** Unity SpikeTilemapGenerator sets, keyed by terrain style id. */
const STYLE_SET: Record<string, string> = {
  'cartoon-classic': 'gray',
  'lucid-dreaming': 'lucid',
  'vexed-grass': 'jesper',
};

const SET_GUIDS: Record<string, { floor: string; ceiling?: string; left?: string; right?: string }> = {
  gray: {
    floor: '851141ef06094434489da6cd2cb3b495',
    ceiling: 'f03158a81fdf98d4ca27177a6377d2dd',
    left: '5b6d98b759b333c4a88bb4f43560796f',
    right: 'ca567473c646332419e09f12c0e29e58',
  },
  bronze: {
    floor: '03faad979c3d93945b27294aa47477ec',
    ceiling: '845157f966d3d6844b66a8f2ea3eb99b',
    left: '4ea64062df9307e4ba6f3d6780eff122',
    right: 'abd6b45ec212cfa4f859acb7d8613984',
  },
  lucid: {
    floor: '79e07f92b1233f84f805ad03da7af225',
    ceiling: '384b485688e91ea47b94bc18b213686f',
    left: '5e52c1c2016eb12468e4ceb46bde3b3f',
    right: '53cd9f35e974aec4a8fc4dc33ac99755',
  },
  jesper: {
    floor: '9be77cf3b50470248a4672a075e06479',
    ceiling: '87be88c1a420dba4eba540fff788bd82',
    left: 'afea5c6f8cdc2c34a80d2315b34e93ed',
    right: '8357d829588da384cbd14ea3f6c88010',
  },
  ice: { floor: '3e24901bea2433442a646bdb5e027491' },
  night: { floor: 'aa1152a198d31104486701497dc9c8c6' },
};

export interface SpikeChoice {
  pe: PaletteEntry;
  flags: number;
  dir: SpikeDir;
}

export interface SpikeDest {
  x: number;
  y: number;
  dirs: SpikeDir[];
  corner: SpikeCorner | null;
  styleId: string;
  /** solid outline lines that paint this dest */
  lines: { x: number; y: number; bit: number }[];
}

export interface SpikeMissing {
  x: number;
  y: number;
  corner: SpikeCorner;
  styleId: string;
}

export interface SpikeRun {
  dir: SpikeDir;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const CORNER_LABEL: Record<SpikeCorner, string> = {
  BL: 'bottom-left inside corner',
  BR: 'bottom-right inside corner',
  TL: 'top-left inside corner',
  TR: 'top-right inside corner',
};

const DIR_OF_BIT: Record<number, SpikeDir> = {
  [LINE_T]: 'floor',
  [LINE_B]: 'ceiling',
  [LINE_R]: 'left',
  [LINE_L]: 'right',
};

/** Empty neighbour the spike sprite sits in, and which way it points. */
export function destOf(x: number, y: number, bit: number): { x: number; y: number; dir: SpikeDir } | null {
  const dir = DIR_OF_BIT[bit];
  if (!dir) return null;
  if (bit === LINE_T) return { x, y: y + 1, dir };
  if (bit === LINE_B) return { x, y: y - 1, dir };
  if (bit === LINE_R) return { x: x + 1, y, dir };
  return { x: x - 1, y, dir };
}

export function cornerOf(dirs: Iterable<SpikeDir>): SpikeCorner | null {
  const s = new Set(dirs);
  const floor = s.has('floor'), ceil = s.has('ceiling'), left = s.has('left'), right = s.has('right');
  if (floor && left && !ceil && !right) return 'BL';
  if (floor && right && !ceil && !left) return 'BR';
  if (ceil && left && !floor && !right) return 'TL';
  if (ceil && right && !floor && !left) return 'TR';
  // A 1-cell-wide well paints floor + both walls into one dest. Still an inside
  // corner: pick the first wall so the tile editor opens. Colliders keep every dir.
  if (floor && left && !ceil) return 'BL';
  if (floor && right && !ceil) return 'BR';
  if (ceil && left && !floor) return 'TL';
  if (ceil && right && !floor) return 'TR';
  return null;
}

export function dirsOfCorner(corner: SpikeCorner): [SpikeDir, SpikeDir] {
  if (corner === 'BL') return ['floor', 'left'];
  if (corner === 'BR') return ['floor', 'right'];
  if (corner === 'TL') return ['ceiling', 'left'];
  return ['ceiling', 'right'];
}

/** Solid neighbour used to pick the terrain theme of a dest / corner. */
export function solidOfDest(x: number, y: number, dir: SpikeDir): [number, number] {
  if (dir === 'floor') return [x, y - 1];
  if (dir === 'ceiling') return [x, y + 1];
  if (dir === 'left') return [x - 1, y];
  return [x + 1, y];
}

export function isSpikeSide(bit: number): boolean {
  return SPIKE_SIDES.includes(bit);
}

function tileEntry(guid: string | undefined): PaletteEntry | null {
  if (!guid) return null;
  try {
    const ta = gameData().sprites.tiles[guid];
    if (!ta?.sprite) return null;
    return { tex: ta.sprite[0], sprite: ta.sprite[1], tile: guid };
  } catch {
    return null;
  }
}

function setIdFor(styleId: string): string {
  return STYLE_SET[styleId.split(':')[0]] ?? 'gray';
}

/** Cardinal spike sprite + flags for a terrain style. Missing wall/ceiling tiles fall back to flipping the floor tile. */
export function cardinalChoice(styleId: string, dir: SpikeDir): SpikeChoice | null {
  const g = SET_GUIDS[setIdFor(styleId)];
  if (!g) return null;
  const own = dir === 'floor' ? g.floor : dir === 'ceiling' ? g.ceiling : dir === 'left' ? g.left : g.right;
  const pe = tileEntry(own);
  if (pe) return { pe, flags: 0, dir };
  const floor = tileEntry(g.floor);
  if (!floor) return null;
  if (dir === 'floor') return { pe: floor, flags: 0, dir };
  if (dir === 'ceiling') return { pe: floor, flags: 2, dir };
  if (dir === 'left') return { pe: floor, flags: 4, dir };
  return { pe: floor, flags: 5, dir };
}

export function cornerTile(styleId: string, corner: SpikeCorner): PaletteEntry | null {
  return styleOverride(styleId.split(':')[0])?.spikeCorners?.[corner] ?? null;
}

function palIndex(doc: LevelDoc, e: PaletteEntry): number {
  const i = doc.palette.findIndex((p) => p.tex === e.tex && p.sprite === e.sprite && (p.tile ?? '') === (e.tile ?? ''));
  if (i >= 0) return i;
  doc.palette.push(e.tile ? { tex: e.tex, sprite: e.sprite, tile: e.tile } : { tex: e.tex, sprite: e.sprite });
  return doc.palette.length - 1;
}

/** Painted-on side lines (slopes ignored). Default is off: only an explicit on-bit counts. */
export function paintedSpikeLines(marks: SpikeLines | undefined, surface: SurfaceLine[]): SurfaceLine[] {
  if (!marks?.length) return [];
  const byCell = new Map<string, number>();
  for (let i = 0; i + 2 < marks.length; i += 3) byCell.set(marks[i] + ',' + marks[i + 1], marks[i + 2]);
  return surface.filter((l) => isSpikeSide(l.bit) && lineMark(byCell.get(l.x + ',' + l.y) ?? 0, l.bit) === 1);
}

const destsCache = new WeakMap<number[], { marks: SpikeLines | undefined; dests: SpikeDest[] }>();

/**
 * Spike cells of a container's painted lines. `surface` must be a generatedSurface result (it carries the
 * style of each cell); the result is shared per surface and marks, so do not mutate it.
 */
export function spikeDests(container: TerrainContainer, surface: number[], marks: SpikeLines | undefined = container.spikeLines): SpikeDest[] {
  const hit = destsCache.get(surface);
  if (hit && hit.marks === marks) return hit.dests;
  const dests = computeSpikeDests(surface, marks);
  destsCache.set(surface, { marks, dests });
  return dests;
}

function computeSpikeDests(surface: number[], marks: SpikeLines | undefined): SpikeDest[] {
  if (!marks?.length) return [];
  const painted = paintedSpikeLines(marks, surfaceLines(surface));
  const groups = new Map<string, SpikeDest>();
  for (const l of painted) {
    const d = destOf(l.x, l.y, l.bit);
    if (!d) continue;
    const styleId = surfaceStyleAt(surface, l.x, l.y);
    if (!styleId) continue;
    const k = d.x + ',' + d.y;
    let g = groups.get(k);
    if (!g) groups.set(k, (g = { x: d.x, y: d.y, dirs: [], corner: null, styleId, lines: [] }));
    if (!g.dirs.includes(d.dir)) g.dirs.push(d.dir);
    g.lines.push({ x: l.x, y: l.y, bit: l.bit });
  }
  for (const g of groups.values()) g.corner = cornerOf(g.dirs);
  return [...groups.values()];
}

export function missingSpikeCorners(dests: SpikeDest[]): SpikeMissing[] {
  const out: SpikeMissing[] = [];
  for (const d of dests) {
    if (!d.corner) continue;
    if (cornerTile(d.styleId, d.corner)) continue;
    out.push({ x: d.x, y: d.y, corner: d.corner, styleId: d.styleId });
  }
  return out;
}

function choiceForDest(d: SpikeDest): { pe: PaletteEntry; flags: number } | null {
  if (d.corner) {
    const pe = cornerTile(d.styleId, d.corner);
    if (pe) return { pe, flags: 0 };
    return cardinalChoice(d.styleId, d.dirs[0]);
  }
  if (d.dirs.length === 1) return cardinalChoice(d.styleId, d.dirs[0]);
  return cardinalChoice(d.styleId, d.dirs[0]);
}

/**
 * Generated spike sprites for the container's spikes layer (flat [x, y, p, f, ...]).
 * Mutates `doc.palette` the same way terrain generation does.
 */
export function generateSpikeTiles(doc: LevelDoc, container: TerrainContainer, surface: number[]): { layerId: string; tiles: number[]; missing: SpikeMissing[]; dests: SpikeDest[] } | null {
  const layer = container.layers.find((l) => l.role === 'spikes');
  if (!layer || !container.spikeLines?.length) return null;
  const dests = spikeDests(container, surface);
  const missing = missingSpikeCorners(dests);
  const tiles: number[] = [];
  for (const d of dests) {
    const ch = choiceForDest(d);
    if (!ch) continue;
    tiles.push(d.x, d.y, palIndex(doc, ch.pe), ch.flags);
  }
  return { layerId: layer.id, tiles, missing, dests };
}

/** Collider runs: consecutive dest cells of one direction (Unity SpikeTilemapGenerator walks). */
export function spikeColliderRuns(dests: SpikeDest[]): SpikeRun[] {
  type Cell = { x: number; y: number; dir: SpikeDir };
  const cells: Cell[] = [];
  for (const d of dests) for (const dir of d.dirs) cells.push({ x: d.x, y: d.y, dir });
  const key = (c: Cell) => c.dir + ':' + c.x + ',' + c.y;
  const set = new Set(cells.map(key));
  const handled = new Set<string>();
  const runs: SpikeRun[] = [];
  const step = (dir: SpikeDir): [number, number] => (dir === 'floor' || dir === 'ceiling' ? [1, 0] : [0, 1]);
  for (const c of cells) {
    const k = key(c);
    if (handled.has(k)) continue;
    const [dx, dy] = step(c.dir);
    let x0 = c.x, y0 = c.y, x1 = c.x, y1 = c.y;
    const walk = (sx: number, sy: number) => {
      let x = c.x, y = c.y;
      for (;;) {
        const nx = x + sx, ny = y + sy;
        const nk = c.dir + ':' + nx + ',' + ny;
        if (!set.has(nk)) break;
        handled.add(nk);
        x = nx;
        y = ny;
      }
      return [x, y] as const;
    };
    handled.add(k);
    [x0, y0] = walk(-dx, -dy);
    [x1, y1] = walk(dx, dy);
    runs.push({ dir: c.dir, x0, y0, x1, y1 });
  }
  return runs;
}

export function applySpikeLines(marks: SpikeLines | undefined, lines: { x: number; y: number; bit: number }[], mode: SpikeMode | GrassMode): SpikeLines | undefined {
  return applyGrassLines(marks, lines.filter((l) => isSpikeSide(l.bit)), mode === 'restore' ? 'erase' : mode);
}

export function spikeEditRequest(container: string, d: SpikeDest, queue?: TileEditRequest['queue']): TileEditRequest | null {
  if (!d.corner) return null;
  const pe = cornerTile(d.styleId, d.corner);
  const custom = pe ? customTileOf(pe) : null;
  return {
    container,
    x: d.x,
    y: d.y,
    layer: 'spike',
    corner: d.corner,
    scope: 'exact',
    mode: custom ? 'edit' : 'new',
    queue,
  };
}

/** First missing (or existing) inside-corner dest, with the rest queued. */
export function spikeEditQueue(containerId: string, dests: SpikeDest[]): TileEditRequest | null {
  // one tile per tileset and corner, whatever the grass colour of the shapes using it
  const seen = new Set<string>();
  const corners = dests.filter((d) => {
    const k = d.styleId.split(':')[0] + '|' + d.corner;
    if (!d.corner || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (!corners.length) return null;
  const missing = corners.filter((d) => !cornerTile(d.styleId, d.corner!));
  const list = missing.length ? missing : corners;
  const [first, ...rest] = list;
  const queue = rest.map((d) => ({ container: containerId, x: d.x, y: d.y, layer: 'spike' as const, corner: d.corner! }));
  return spikeEditRequest(containerId, first, queue.length ? queue : undefined);
}

export function destAt(dests: SpikeDest[], p: [number, number]): SpikeDest | null {
  const x = Math.floor(p[0]), y = Math.floor(p[1]);
  return dests.find((d) => d.x === x && d.y === y) ?? null;
}

/** Write a corner tile into the style override (preview, then saveStyleOverride). */
export function linkSpikeCorner(o: StyleOverride, corner: SpikeCorner, pe: PaletteEntry): void {
  (o.spikeCorners ??= {})[corner] = pe.tile ? { tex: pe.tex, sprite: pe.sprite, tile: pe.tile } : { tex: pe.tex, sprite: pe.sprite };
}

/** L-outline of a dest cell for the tile editor guide (cell units, y up). */
export function cornerGuide(corner: SpikeCorner): { lines: [number, number, number, number][]; fill: [number, number][] } {
  if (corner === 'BL') return { lines: [[0, 0, 1, 0], [0, 0, 0, 1]], fill: [[-1, 0], [0, -1], [-1, -1]] };
  if (corner === 'BR') return { lines: [[0, 0, 1, 0], [1, 0, 1, 1]], fill: [[1, 0], [0, -1], [1, -1]] };
  if (corner === 'TL') return { lines: [[0, 1, 1, 1], [0, 0, 0, 1]], fill: [[-1, 0], [0, 1], [-1, 1]] };
  return { lines: [[0, 1, 1, 1], [1, 0, 1, 1]], fill: [[1, 0], [0, 1], [1, 1]] };
}
